/**
 * 建群弹窗（对齐「UI 半成品」）：
 * - 群名 Input
 * - 成员从好友列表多选（不再手输 OpenIM userID）
 * - 确认后只调 gateway.createGroup → OpenIM SDK
 * - 不调用任何 /api/im/* 建群接口（后端本就不该有）
 */
import { useEffect, useState } from "react";
import {
  Avatar,
  Checkbox,
  Flex,
  Input,
  List,
  Modal,
  Typography,
  message,
} from "antd";
import type { FriendBrief } from "../types";

type Props = {
  /** 控制 Modal 显隐 */
  open: boolean;
  /** 离线时禁止提交 */
  disabled?: boolean;
  onClose: () => void;
  /** 打开弹窗时拉取可选好友（OpenIM 好友，不是 Nexora 目录） */
  loadFriends: () => Promise<FriendBrief[]>;
  /**
   * 建群：返回 groupID。
   * 实现应在 hook 里调 gateway.createGroup，成功后刷新会话。
   */
  onCreate: (groupName: string, memberUserIDs: string[]) => Promise<string>;
};

/** Avatar 无图时用展示名首字 */
function initialOf(name: string): string {
  const t = (name || "").trim();
  return t ? t.slice(0, 1) : "?";
}

export function CreateGroupModal({
  open,
  disabled,
  onClose,
  loadFriends,
  onCreate,
}: Props) {
  // 群名称草稿
  const [name, setName] = useState("");
  // 可选好友（打开时加载）
  const [friends, setFriends] = useState<FriendBrief[]>([]);
  // 已勾选的 OpenIM userID
  const [selected, setSelected] = useState<string[]>([]);
  // 好友列表加载中
  const [loading, setLoading] = useState(false);
  // 建群请求进行中（防连点）
  const [busy, setBusy] = useState(false);

  // 每次打开：清空表单并重新拉好友，避免列表过期
  useEffect(() => {
    if (!open) return;
    setName("");
    setSelected([]);
    setLoading(true);
    void loadFriends()
      .then(setFriends)
      .catch((e: Error) => message.error(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [open, loadFriends]);

  /** 勾选/取消某一好友；用 Set 去重避免重复 ID */
  const toggle = (userID: string, checked: boolean) => {
    setSelected((prev) =>
      checked
        ? [...new Set([...prev, userID])]
        : prev.filter((id) => id !== userID),
    );
  };

  /** 确认建群：只把 selected 的 userID 交给上层 SDK 封装 */
  const submit = async () => {
    const groupName = name.trim();
    if (!groupName) {
      message.warning("请填写群名称");
      return;
    }
    // OpenIM 允许成员为空（仅自己）；产品上建议至少选 1 人，此处放宽
    setBusy(true);
    try {
      const gid = await onCreate(groupName, selected);
      message.success(gid ? `已建群 ${gid}` : "已建群");
      onClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="创建群聊"
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      confirmLoading={busy}
      okButtonProps={{ disabled: disabled || !name.trim() }}
      destroyOnClose
      width={420}
    >
      <Flex vertical gap={12}>
        <div>
          <Typography.Text type="secondary">群名称</Typography.Text>
          <Input
            value={name}
            maxLength={64}
            placeholder="例如：项目讨论组"
            disabled={disabled || busy}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={() => void submit()}
          />
        </div>

        <Typography.Text type="secondary">
          选择成员（来自好友列表，已选 {selected.length} 人）
        </Typography.Text>

        <div style={{ maxHeight: 280, overflow: "auto" }}>
          <List
            loading={loading}
            locale={{ emptyText: "暂无好友，请先去「搜人」加好友" }}
            dataSource={friends}
            renderItem={(f) => {
              const checked = selected.includes(f.userID);
              return (
                <List.Item
                  style={{ cursor: "pointer", padding: "8px 4px" }}
                  onClick={() => toggle(f.userID, !checked)}
                >
                  <Flex align="center" gap={10} style={{ width: "100%" }}>
                    <Checkbox
                      checked={checked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggle(f.userID, e.target.checked)}
                    />
                    <Avatar
                      size={32}
                      src={f.faceURL || undefined}
                      style={{ background: "#1e293b" }}
                    >
                      {initialOf(f.displayName)}
                    </Avatar>
                    <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                      <Typography.Text ellipsis>{f.displayName}</Typography.Text>
                      <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                        {f.userID}
                      </Typography.Text>
                    </Flex>
                  </Flex>
                </List.Item>
              );
            }}
          />
        </div>
      </Flex>
    </Modal>
  );
}